// Provenance: repo:tds-reconciliation-portal:frontend/src/components/RunInsights.tsx
// (ADR-0005) — restyled (ADR-0010). Run ids became UUID strings.
import { AlertTriangle, CalendarClock } from 'lucide-react'

import { useCreditAtRisk, useTiming } from '../api/hooks'
import { Money } from './Money'

/** Quarter-wise timing tie-out + credit-at-risk — the two most explanatory
 *  lenses on the gap (adapted from the workbook's Timing & Status + Summary).
 *
 *  `active` must be false while this is mounted but not visible: both
 *  endpoints scan the run, and in the predecessor they were doing it on every
 *  page load for a panel nobody had opened. The Drawer mounts content only
 *  while open, but the guard stays — the contract belongs to this component,
 *  not to whoever happens to host it. */
export function RunInsights({ runId, active = true }: {
  runId: string | null
  active?: boolean
}) {
  const timing = useTiming(runId, active)
  const risk = useCreditAtRisk(runId, active)

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <div className="rounded-lg border border-border p-4">
        <div className="mb-2 flex items-center gap-2">
          <CalendarClock size={18} />
          <h3 className="text-sm font-semibold text-foreground">Timing — where the gap sits in the year</h3>
        </div>
        {timing.data && (
          <>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-muted-foreground">
                  <th className="py-1 font-medium">Quarter</th>
                  <th className="py-1 text-right font-medium">26AS</th>
                  <th className="py-1 text-right font-medium">Books</th>
                  <th className="py-1 text-right font-medium">Difference</th>
                </tr>
              </thead>
              <tbody>
                {timing.data.quarters.map((q) => (
                  <tr key={q.quarter}>
                    <td className="py-1 font-semibold">{q.quarter}</td>
                    <td className="py-1 text-right"><Money v={q.tds_26as} /></td>
                    <td className="py-1 text-right"><Money v={q.tds_books} /></td>
                    <td className="py-1 text-right"><Money v={q.diff} colorize /></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mt-2 text-xs text-muted-foreground">{timing.data.narrative}</p>
          </>
        )}
      </div>

      <div className="space-y-4">
        {risk.data && Number(risk.data.at_risk) > 0 && (
          <div className="rounded-lg border border-orange-300 bg-orange-50 p-4">
            <p className="flex items-center gap-2 text-sm font-semibold text-orange-900">
              <AlertTriangle size={18} />
              Credit at risk: <Money v={risk.data.at_risk} bold />
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Of the 26AS TDS, this much is booking-status U/O/Z — the deductor has not filed a
              final (F) credit yet, so it may not be available until they correct their TDS return.
            </p>
            <div className="mt-2 flex flex-wrap gap-4">
              {risk.data.by_status.map((s) => (
                <span key={s.status} className="text-xs">
                  <b>{s.status}</b> {s.count} · <Money v={s.tds} className="text-xs" />
                </span>
              ))}
            </div>
          </div>
        )}
        {risk.data && Number(risk.data.at_risk) === 0 && (
          <div className="rounded-lg border border-border p-4">
            <p className="text-sm font-semibold text-emerald-700">
              All 26AS credits are final (status F) — nothing at risk.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
